import React, { useState, useEffect } from "react";
import {
  Document, Page, Image, Text, View, StyleSheet, Font,
  Svg, Path, Circle, Rect,
} from '@react-pdf/renderer';
import CircularProgress from '@mui/material/CircularProgress';
import { withRouter } from "../../providers/withRouter";
import PDFHandler from "../../components/pdfs/PDFHandler";
import RobotoRegular from "../../fonts/Poppins-Regular.ttf";
import RobotoBold from "../../fonts/Poppins-Bold.ttf";
import RobotoItalic from "../../fonts/Poppins-Italic.ttf";
import RobotoBoldItalic from "../../fonts/Poppins-BoldItalic.ttf";
import logoIntegracces from "../../images/logo integracces.png";
import firmaArquitecto from "../../images/firma arquitecto.png";
import sealStamp from "../../images/stamp-accesibilidad.png";
import { getVisitSheetCai, getVisitSheetIdFromSale } from "../../services";

// Local Poppins TTFs back the Inter / JetBrainsMono families referenced in the
// .repl template. The mono look is lost (no IBM Plex Mono in the repo), but the
// layout renders with one font family.
Font.register({
  family: 'Poppins',
  fonts: [
    { src: RobotoRegular, fontWeight: 'normal' },
    { src: RobotoBold, fontWeight: 'bold' },
    { src: RobotoItalic, fontWeight: 'normal', fontStyle: 'italic' },
    { src: RobotoBoldItalic, fontWeight: 'bold', fontStyle: 'italic' },
  ],
});
Font.register({ family: 'Poppins-Bold', src: RobotoBold });

Font.register({
  family: 'Inter',
  fonts: [
    { src: RobotoRegular, fontWeight: 'normal' },
    { src: RobotoBold, fontWeight: 'bold' },
    { src: RobotoItalic, fontWeight: 'normal', fontStyle: 'italic' },
    { src: RobotoBoldItalic, fontWeight: 'bold', fontStyle: 'italic' },
  ],
});
Font.register({ family: 'Inter-Bold', src: RobotoBold });

Font.register({
  family: 'JetBrainsMono',
  fonts: [
    { src: RobotoRegular, fontWeight: 'normal' },
    { src: RobotoBold, fontWeight: 600 },
    { src: RobotoBold, fontWeight: 'bold' },
  ],
});
Font.register({ family: 'JetBrainsMono-Bold', src: RobotoBold });
Font.register({ family: 'JetBrainsMono-SemiBold', src: RobotoBold });

const F = {
  sans: 'Inter',
  sansBold: 'Inter-Bold',
  mono: 'JetBrainsMono',
  monoBold: 'JetBrainsMono-Bold',
  monoSemiBold: 'JetBrainsMono-SemiBold',
};

const C = {
  blue: '#1F7EBF',
  blueDeep: '#155A8C',
  blueDarker: '#0E456E',
  green: '#8FC93F',
  greenDeep: '#6BA02A',
  greenDarker: '#4F7B1F',
  red: '#D14843',
  redDeep: '#A8332E',
  amberFg: '#8A5A00',
  ink: '#0F1620',
  ink2: '#2A3340',
  muted: '#6E6E6E',
  line: '#E4E7EC',
  lineStrong: '#C9CFD7',
  surface: '#FFFFFF',
  surface2: '#F8F9FA',
  surface3: '#F1F4F7',
  tintBlue: '#EAF3FB',
  tintBlueSection: '#F0F7FC',
  tintBlueLine: '#C9DFEF',
  tintGreen: '#F0F8E2',
  tintGreenLine: '#D2E8AC',
  tintRed: '#FCEDEC',
  tintRedLine: '#F1C9C7',
  amberBg: '#FFF6E5',
  amberLine: '#F0D89A',
};

const gradeBucket = (pct) => {
  const n = parseInt(pct, 10);
  if (isNaN(n)) return 'partial';
  if (n > 70) return 'pass';
  if (n < 50) return 'fail';
  return 'partial';
};

const bucketFg = (b) => b === 'pass' ? C.greenDarker : b === 'fail' ? C.redDeep : C.amberFg;
const bucketFgAccent = (b) => b === 'pass' ? C.greenDeep : b === 'fail' ? C.redDeep : C.amberFg;
const bucketBg = (b) => b === 'pass' ? C.tintGreen : b === 'fail' ? C.tintRed : C.amberBg;
const bucketBorder = (b) => b === 'pass' ? C.tintGreenLine : b === 'fail' ? C.tintRedLine : C.amberLine;

const styles = StyleSheet.create({
  contentPage: {
    paddingTop: '18mm',
    paddingBottom: '20mm',
    paddingHorizontal: '16mm',
    backgroundColor: C.surface,
    fontFamily: F.sans,
    fontSize: 10,
    // lineHeight: 1.15,
    color: C.ink,
  },
  pageFooter: {
    position: 'absolute',
    bottom: '10mm',
    left: '16mm',
    right: '16mm',
    fontSize: 8,
    color: C.blueDeep,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  pageFooterLeft: { fontFamily: F.sans, fontSize: 8, color: C.blueDeep },
  pageFooterRight: { fontFamily: F.mono, fontSize: 8, color: C.blueDeep },

  cover: {
    borderWidth: 1,
    borderColor: C.line,
    borderTopWidth: 4,
    borderTopColor: C.blue,
    borderRadius: 6,
    overflow: 'visible',
    position: 'relative',
  },
  coverTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: C.line,
    backgroundColor: C.tintBlueSection,
  },
  coverBrand: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  coverBrandImg: { height: 38 },
  coverMeta: { flexDirection: 'column', alignItems: 'flex-end', gap: 4 },
  tag: {
    fontFamily: F.monoSemiBold,
    fontSize: 8,
    letterSpacing: 0.4,
    color: C.surface,
    backgroundColor: C.blue,
    paddingVertical: 3,
    paddingHorizontal: 9,
    borderRadius: 999,
    textTransform: 'uppercase',
  },
  coverDocId: { fontFamily: F.mono, fontSize: 8.5, color: C.blueDeep },

  coverTitleRow: {
    paddingTop: 16,
    paddingHorizontal: 18,
    paddingBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
  },
  coverTitleBlock: { flexGrow: 1, flexShrink: 1 },
  coverEyebrow: {
    fontFamily: F.monoSemiBold,
    fontSize: 8.5,
    letterSpacing: 0.7,
    color: C.blueDeep,
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  coverTitle: {
    fontSize: 24,
    fontFamily: F.sansBold,
    color: C.blueDeep,
    lineHeight: 1.1,
    marginBottom: 4,
  },
  coverSubtitle: { fontFamily: F.sans, fontSize: 11, color: C.ink2 },

  coverScoreInline: { flexDirection: 'column', alignItems: 'flex-end', gap: 1 },
  coverScoreLabel: {
    fontFamily: F.monoSemiBold,
    fontSize: 7.5,
    letterSpacing: 0.7,
    textTransform: 'uppercase',
  },
  coverScoreValue: { flexDirection: 'row', alignItems: 'baseline' },
  coverScoreNum: { fontFamily: F.monoBold, fontSize: 40, lineHeight: 0.9 },
  coverScorePct: { fontFamily: F.monoSemiBold, fontSize: 20, marginLeft: 2, opacity: 0.85 },

  coverFields: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: C.line,
  },
  coverField: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRightWidth: 1,
    borderRightColor: C.line,
    flexGrow: 1,
  },
  coverFieldLast: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    flexGrow: 1,
  },
  fieldLabel: {
    fontFamily: F.monoSemiBold,
    fontSize: 6.5,
    letterSpacing: 0.6,
    color: C.blueDeep,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  fieldValue: { fontFamily: F.mono, fontSize: 8, color: C.ink },

  section: { marginTop: 16, marginBottom: 8 },
  sectionHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderBottomWidth: 2,
    borderBottomColor: C.blue,
    paddingBottom: 6,
    marginBottom: 11,
  },
  sectionNum: {
    fontFamily: F.monoBold,
    fontSize: 9,
    color: C.surface,
    backgroundColor: C.blue,
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 4,
    letterSpacing: 0.4,
  },
  sectionTitle: {
    fontSize: 13.5,
    fontFamily: F.sansBold,
    color: C.blueDeep,
    flexGrow: 1,
  },
  paragraph: {
    color: C.ink2,
    marginBottom: 6,
    // lineHeight: 1.0,
    textAlign: 'justify',
  },
  bold: { fontFamily: F.sansBold, color: C.blueDeep },
  italic: { fontStyle: 'italic' },

  dataGrid: {
    borderWidth: 1,
    borderColor: C.tintBlueLine,
    borderRadius: 6,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  dataCell: {
    width: '50%',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: C.tintBlueLine,
    borderRightWidth: 1,
    borderRightColor: C.tintBlueLine,
  },
  dataCellRight: { borderRightWidth: 0 },
  dataCellBottom: { borderBottomWidth: 0 },
  dataCellFull: {
    width: '100%',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: C.tintBlueLine,
  },
  dataCellLabel: {
    fontFamily: F.monoSemiBold,
    fontSize: 6.5,
    letterSpacing: 0.7,
    color: C.blueDeep,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  dataCellValue: { fontFamily: F.mono, fontSize: 8.5, color: C.ink },

  dim: { marginTop: 14 },
  dimHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 9,
    paddingBottom: 5,
    borderBottomWidth: 1.5,
    borderBottomColor: C.blue,
  },
  dimIconWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: 13,
    height: 13,
  },
  dimName: {
    flexGrow: 1,
    fontSize: 11.5,
    fontFamily: F.sansBold,
    color: C.blueDeep,
  },
  dimGrade: {
    flexDirection: 'row',
    alignItems: 'baseline',
    paddingVertical: 3,
    paddingHorizontal: 10,
    borderRadius: 999,
    borderWidth: 1,
  },
  dimGradeText: { fontFamily: F.monoBold, fontSize: 11 },
  dimGradePct: { fontFamily: F.monoSemiBold, fontSize: 8, marginLeft: 2, opacity: 0.75 },

  dimTable: { marginTop: 6, marginBottom: 10 },
  dimTableHead: {
    flexDirection: 'row',
    backgroundColor: C.tintBlueSection,
    borderTopWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: C.tintBlueLine,
    borderRadius: 4,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginBottom: 3,
  },
  dimTableHeadCell: {
    fontFamily: F.sansBold,
    fontSize: 6.8,
    letterSpacing: 0.7,
    color: C.blueDeep,
    textTransform: 'uppercase',
  },
  dimRow: {
    flexDirection: 'row',
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderWidth: 0.5,
    borderColor: C.line,
    borderRadius: 6,
    marginBottom: 3,
    backgroundColor: C.surface,
  },
  dimRowAlt: { backgroundColor: C.surface2 },
  cellParametro: { width: '26%', paddingRight: 8, fontFamily: F.sans, fontSize: 8.5, color: C.ink },
  cellArticulo: { width: '22%', paddingRight: 8, fontFamily: F.sans, fontSize: 7, color: C.muted },
  cellRequisito: { width: '22%', paddingRight: 8, fontFamily: F.sans, fontSize: 8.5, color: C.ink2 },
  cellDato: {
    width: '16%', paddingRight: 8, fontSize: 8,
    fontFamily: F.monoSemiBold, color: C.ink,
  },
  cellEstado: { width: '14%', justifyContent: 'center', alignItems: 'flex-start' },
  estadoChipWrap: {
    paddingVertical: 2,
    paddingHorizontal: 7,
    borderRadius: 999,
    borderWidth: 0.5,
    alignSelf: 'flex-start',
  },
  estadoChipText: {
    fontFamily: F.monoBold,
    fontSize: 6.8,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    textAlign: 'center',
  },

  incumpHead: {
    flexDirection: 'row',
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: C.lineStrong,
    marginBottom: 4,
  },
  incumpHeadCell: {
    fontFamily: F.monoBold,
    fontSize: 7.5,
    letterSpacing: 0.7,
    textTransform: 'uppercase',
    color: C.blueDeep,
  },
  incumpRow: {
    flexDirection: 'row',
    paddingVertical: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: C.line,
    alignItems: 'flex-start',
  },
  incumpIdx: { width: 22, fontFamily: F.monoBold, fontSize: 9, color: C.redDeep },
  incumpDim: {
    width: 90,
    fontFamily: F.monoBold,
    fontSize: 8,
    letterSpacing: 0.5,
    color: C.redDeep,
    textTransform: 'uppercase',
  },
  incumpParam: { flexGrow: 1, paddingRight: 8 },
  incumpParamTitle: { fontFamily: F.sansBold, fontSize: 9.5, color: C.redDeep },
  incumpDetail: { fontFamily: F.mono, fontSize: 8.5, color: C.redDeep, opacity: 0.75, marginTop: 1 },
  incumpArticleCol: {
    width: 130,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  incumpArticlePill: {
    backgroundColor: C.tintRed,
    borderWidth: 0.5,
    borderColor: C.tintRedLine,
    borderRadius: 4,
    paddingVertical: 3,
    paddingHorizontal: 7,
  },
  incumpArticleText: {
    fontFamily: F.monoSemiBold,
    fontSize: 8,
    color: C.redDeep,
  },

  resultBox: {
    marginTop: 8,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  resultScore: {
    borderWidth: 1,
    borderColor: C.line,
    borderRadius: 6,
    paddingVertical: 12,
    paddingHorizontal: 14,
    alignItems: 'center',
    minWidth: 160,
  },
  resultLabel: {
    fontFamily: F.monoSemiBold,
    fontSize: 7.8,
    letterSpacing: 0.7,
    textTransform: 'uppercase',
    color: C.muted,
    marginBottom: 4,
  },
  resultValue: { flexDirection: 'row', alignItems: 'baseline' },
  resultNum: { fontFamily: F.monoBold, fontSize: 28, lineHeight: 1, color: C.blue },
  resultPct: { fontFamily: F.monoBold, fontSize: 18, color: C.muted, marginLeft: 2 },

  signoff: {
    marginTop: 14,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: C.line,
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 18,
    alignItems: 'flex-start',
  },
  signoffPlace: { width: 240 },
  signoffPill: {
    alignSelf: 'flex-start',
    fontFamily: F.monoSemiBold,
    fontSize: 8,
    letterSpacing: 0.5,
    color: C.surface,
    backgroundColor: C.greenDeep,
    paddingVertical: 3,
    paddingHorizontal: 9,
    borderRadius: 999,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  signoffDisclaimer: {
    fontFamily: F.sans,
    fontSize: 10,
    color: C.ink2,
    lineHeight: 1.0,
    marginTop: 4,
  },
  signature: {
    width: 220,
    borderWidth: 1,
    borderColor: C.tintGreenLine,
    borderRadius: 6,
    paddingVertical: 30,
    paddingHorizontal: 12,
    backgroundColor: C.surface,
    alignItems: 'center',
  },
  signatureImg: { width: 32, marginBottom: 4 },
  signatureName: { fontFamily: F.sansBold, fontSize: 9},
  signatureSub: { fontFamily: F.mono, fontSize: 9, color: C.ink2, marginTop: 1 },
  signatureRole: { fontSize: 8.5 },

  stamp: {
    position: 'absolute',
    top: '36mm',
    right: '6mm',
    width: '48mm',
    height: '48mm',
    opacity: 0.30,
  },
});

const dimStrokeProps = (color) => ({
  fill: 'none',
  stroke: color,
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

const DIM_ICONS = {
  itinerario: (color) => (
    <Svg viewBox="0 0 24 24" width={13} height={13}>
      <Circle cx="4" cy="20" r="1.3" fill={color} />
      <Path d="M4 20Q18 16 12 12Q6 8 15 6" {...dimStrokeProps(color)} />
      <Path d="M12 5L15 6L13 8" {...dimStrokeProps(color)} />
      <Path d="M17 3L21 7M21 3L17 7" {...dimStrokeProps(color)} />
    </Svg>
  ),
  acceso: (color) => (
    <Svg viewBox="0 0 24 24" width={13} height={13}>
      <Path d="M14 4h5a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-5" {...dimStrokeProps(color)} />
      <Path d="M3 12h12" {...dimStrokeProps(color)} />
      <Path d="M11 8L15 12L11 16" {...dimStrokeProps(color)} />
    </Svg>
  ),
  desnivel: (color) => (
    <Svg viewBox="0 0 24 24" width={13} height={13}>
      <Path d="M3 19H8V14H14V9H21" {...dimStrokeProps(color)} />
    </Svg>
  ),
  rampa: (color) => (
    <Svg viewBox="0 0 24 24" width={13} height={13}>
      <Path d="M3 19H21V7Z" {...dimStrokeProps(color)} />
      <Path d="M6 10L15 4" {...dimStrokeProps(color)} />
      <Path d="M12 4L15 4L14 7" {...dimStrokeProps(color)} />
    </Svg>
  ),
  ascensor: (color) => (
    <Svg viewBox="0 0 24 24" width={13} height={13}>
      <Rect x="4" y="3" width="16" height="18" rx="1.5" {...dimStrokeProps(color)} />
      <Path d="M12 4v16" {...dimStrokeProps(color)} />
      <Path d="M8 10L9.5 8L11 10" {...dimStrokeProps(color)} />
      <Path d="M9.5 8v4" {...dimStrokeProps(color)} />
      <Path d="M13 14L14.5 16L16 14" {...dimStrokeProps(color)} />
      <Path d="M14.5 12v4" {...dimStrokeProps(color)} />
    </Svg>
  ),
  'plataformas-verticales': (color) => (
    <Svg viewBox="0 0 24 24" width={13} height={13}>
      <Path d="M5 4v16M19 4v16" {...dimStrokeProps(color)} />
      <Rect x="6" y="14" width="12" height="3" rx="0.5" {...dimStrokeProps(color)} />
      <Path d="M9 11V6" {...dimStrokeProps(color)} />
      <Path d="M9 6L7.5 7.5" {...dimStrokeProps(color)} />
      <Path d="M9 6L10.5 7.5" {...dimStrokeProps(color)} />
      <Path d="M15 11V6" {...dimStrokeProps(color)} />
      <Path d="M15 6L13.5 7.5" {...dimStrokeProps(color)} />
      <Path d="M15 6L16.5 7.5" {...dimStrokeProps(color)} />
    </Svg>
  ),
  'plataformas-inclinadas': (color) => (
    <Svg viewBox="0 0 24 24" width={13} height={13}>
      <Path d="M3 19L9 14" {...dimStrokeProps(color)} />
      <Path d="M13 11L21 4" {...dimStrokeProps(color)} />
      <Rect x="6" y="11" width="12" height="3" rx="0.4" {...dimStrokeProps(color)} />
      <Path d="M3 10L11 4" {...dimStrokeProps(color)} />
      <Path d="M8 4L11 4L10 7" {...dimStrokeProps(color)} />
    </Svg>
  ),
  aparcamiento: (color) => (
    <Svg viewBox="0 0 24 24" width={13} height={13}>
      <Rect x="4" y="4" width="16" height="16" rx="2.5" {...dimStrokeProps(color)} />
      <Path d="M10 17V7h3.2a2.8 2.8 0 0 1 0 5.6H10" {...dimStrokeProps(color)} />
    </Svg>
  ),
};

const resolveDimIconKey = (name) => {
  const n = (name || '').toLowerCase().trim();
  if (n.startsWith('itinerario')) return 'itinerario';
  if (n.startsWith('acceso')) return 'acceso';
  if (n.startsWith('desnivel')) return 'desnivel';
  if (n.startsWith('rampa')) return 'rampa';
  if (n.startsWith('ascensor')) return 'ascensor';
  if (n.includes('plataformas') && n.includes('vertical')) return 'plataformas-verticales';
  if (n.includes('plataformas') && n.includes('inclinad')) return 'plataformas-inclinadas';
  if (n.startsWith('aparcamiento')) return 'aparcamiento';
  return null;
};

const categoryOrderIndex = (name) => { // listado por el que deseo ordenar las categorias que aparecen en el pdf
  const n = (name || '').toLowerCase().trim();
  if (n.startsWith('itinerario')) return 0;
  if (n.startsWith('acceso')) return 1;
  if (n.startsWith('desnivel')) return 2;
  if (n.startsWith('rampa')) return 3;
  if (n.startsWith('ascensor')) return 4;
  if ((n.includes('plataforma') || n.includes('plataformas')) && n.includes('vertical')) return 5;
  if ((n.includes('plataforma') || n.includes('plataformas')) && n.includes('inclinad')) return 6;
  if (n.startsWith('aparcamiento')) return 7;
  return 999;
};

const sortDimsByCategory = (dims) => // Ordenar por categoría, para tener el orden distinto al de la base de datos
  [...dims].sort((a, b) => categoryOrderIndex(a.nombre_categoria) - categoryOrderIndex(b.nombre_categoria));

const normalizeAnswersCai = (raw) => { // agrupar por categoría, las answer siguientes que no tienen categoria.
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : Object.values(raw);
  if (list.length === 0) return [];

  // Detectar si ya viene anidado (subquestions como array/objeto con contenido).
  // Ojo: NO usar questionCai aquí — es la pregunta-padre relacionada, no las hijas.
  const alreadyNested = list.some((item) => {
    const subs = item?.subquestions;
    if (Array.isArray(subs)) return subs.length > 0;
    if (subs && typeof subs === 'object') return Object.keys(subs).length > 0;
    return false;
  });
  if (alreadyNested) return list;

  // Ordenar por id ascendente para recuperar el orden original de la BD
  // (header de categoría seguido de sus subquestions).
  const sorted = [...list].sort((a, b) => {
    const ai = Number(a?.id);
    const bi = Number(b?.id);
    if (Number.isNaN(ai) || Number.isNaN(bi)) return 0;
    return ai - bi;
  });

  const groups = [];
  let current = null;
  for (const row of sorted) {
    if (!row) continue;
    const hasCategory = row.nombre_categoria && String(row.nombre_categoria).trim() !== '';
    if (hasCategory) {
      current = { ...row, subquestions: [] };
      groups.push(current);
    } else if (current) {
      current.subquestions.push(row);
    }
  }
  return groups;
};

const DimIcon = ({ name, color }) => {
  const key = resolveDimIconKey(name);
  if (!key || !DIM_ICONS[key]) return null;
  return DIM_ICONS[key](color);
};

const ESTADO_LABELS = {
  cumple: { label: 'Cumple', fg: C.greenDarker, bg: C.tintGreen, border: C.tintGreenLine, prefix: 'OK' },
  'no cumple': { label: 'No cumple', fg: C.redDeep, bg: C.tintRed, border: C.tintRedLine, prefix: 'X' },
  'no procede': { label: 'No procede', fg: C.muted, bg: C.surface3, border: C.line, prefix: '–' },
};

const EstadoChip = ({ cumplimiento }) => {
  const def = ESTADO_LABELS[cumplimiento] || ESTADO_LABELS['no procede'];
  return (
    <View style={[
      styles.estadoChipWrap,
      { backgroundColor: def.bg, borderColor: def.border },
    ]}>
      <Text style={[styles.estadoChipText, { color: def.fg }]}>
        {def.label}
      </Text>
    </View>
  );
};

const deriveIncumplimientos = (answersCai, articulosIncumplidosLegacy) => {
  const dims = sortDimsByCategory(normalizeAnswersCai(answersCai));
  if (dims.length === 0) {
    return (articulosIncumplidosLegacy || []).map((a, i) => ({
      idx: i + 1,
      dim: '',
      paramTitle: `Artículo ${a.numero || '__________________'}`,
      detail: a.descripcion || '__________________',
      article: a.numero || '',
    }));
  }
  const rows = [];
  let idx = 0;
  for (const dim of dims) {
    const subs = Object.values(dim.subquestions || {});
    for (const sq of subs) {
      if (sq?.cumplimiento === 'no cumple') {
        idx += 1;
        const requisito = sq.questionCai?.requisito || '';
        rows.push({
          idx,
          dim: dim.nombre_categoria || '',
          paramTitle: sq.parametro || '',
          detail: `${requisito ? requisito + ' · ' : ''}${sq.resp || ''}`,
          article: sq.anexo || '',
        });
      }
    }
  }
  return rows;
};

const CertificadoAccesibilidad = (props) => {
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const sale = await getVisitSheetIdFromSale(props.params.id);
    const visitSheetCai_id = sale.detailsCai[0]?.visitSheetCai_id;
    const response = await getVisitSheetCai(visitSheetCai_id);
    setFormData(response.data);
    setIsLoading(false);
  };

  const data = formData || {};
  const {
    comunidad, fecha, solicitante, cif, direccion, municipio,
    referenciaCatastral, anoConstruction, perfilEdificatorio,
    numViviendas, tieneAscensor, numAscensores, fechaVisita,
    articulosIncumplidos, lugarFirma, diaFirma, mesFirma, anoFirma,
    porcentaje_total, docId, answersCai,
  } = {
    comunidad: data.propertie || '',
    fecha: data.visitDate || '',
    solicitante: data.solicitante || '',
    cif: data.cif || '',
    direccion: data.direccion || '',
    municipio: data.municipio || '',
    referenciaCatastral: data.referenciaCatastral || '',
    anoConstruction: data.anoConstruction || '',
    perfilEdificatorio: data.perfilEdificatorio || '',
    numViviendas: data.numViviendas || '',
    tieneAscensor: data.tieneAscensor || 'No',
    numAscensores: data.numAscensores || '',
    fechaVisita: data.visitDate || '',
    articulosIncumplidos: data.articulosIncumplidos || [],
    lugarFirma: data.propertie?.municipio || '',
    diaFirma: new Date(data.visitDate).getDate() || '',
    mesFirma: new Date(data.visitDate).toLocaleString('es-ES', { month: 'long' }) || '',
    anoFirma: new Date(data.visitDate).getFullYear() || '',
    porcentaje_total: data.porcentaje_total || '',
    docId: data.docId || '',
    answersCai: data.answersCai || [],
  };

  const totalNum = parseInt(porcentaje_total, 10);
  const totalBucket = gradeBucket(porcentaje_total);
  const totalFg = bucketFg(totalBucket);
  const totalFgAccent = bucketFgAccent(totalBucket);
  const stampVisible = !isNaN(totalNum) && totalNum >= 80;
  const incumplimientos = deriveIncumplimientos(answersCai, articulosIncumplidos);
  const fechaStr = fecha ? new Date(fecha).toLocaleDateString('es-ES') : '';

  return (
    <>
      {isLoading ? (
        <CircularProgress style={{ color: '#92004D', position: 'absolute', top: '50%', left: '50%' }} />
      ) : (
        <PDFHandler>
          <Document>
            <Page size="A4" style={styles.contentPage}>

              <View style={styles.cover} wrap={false}>
                {stampVisible && <Image src={sealStamp} style={styles.stamp} />}

                <View style={styles.coverTop}>
                  <View style={styles.coverBrand}>
                    <Image src={logoIntegracces} style={styles.coverBrandImg} />
                  </View>
                  <View style={styles.coverMeta}>
                    <Text style={styles.tag}>Certificado · Accesibilidad</Text>
                    {!!docId && <Text style={styles.coverDocId}>{docId}</Text>}
                  </View>
                </View>

                <View style={styles.coverTitleRow}>
                  <View style={styles.coverTitleBlock}>
                    <Text style={styles.coverEyebrow}>DECRET 209/2023 · Codi d'accessibilitat de Catalunya</Text>
                    <Text style={styles.coverTitle}>Certificado de Accesibilidad</Text>
                    <Text style={styles.coverSubtitle}>Certificado Indicativo del Grado de Accesibilidad del Edificio</Text>
                  </View>
                  <View style={styles.coverScoreInline}>
                    <Text style={[styles.coverScoreLabel, { color: totalFg }]}>Grado de accesibilidad</Text>
                    <View style={styles.coverScoreValue}>
                      <Text style={[styles.coverScoreNum, { color: totalFgAccent }]}>
                        {isNaN(totalNum) ? '__' : totalNum}
                      </Text>
                      <Text style={[styles.coverScorePct, { color: totalFg }]}>%</Text>
                    </View>
                  </View>
                </View>

                <View style={styles.coverFields}>
                  <View style={styles.coverField}>
                    <Text style={styles.fieldLabel}>Comunidad</Text>
                    <Text style={styles.fieldValue}>
                      {(comunidad && comunidad.razonSocial) || '__'}
                    </Text>
                  </View>
                  <View style={styles.coverFieldLast}>
                    <Text style={styles.fieldLabel}>Fecha</Text>
                    <Text style={styles.fieldValue}>{fechaStr || '__'}</Text>
                  </View>
                </View>
              </View>

              <View style={styles.section}>
                <View style={styles.sectionHead}>
                  <Text style={styles.sectionNum}>01</Text>
                  <Text style={styles.sectionTitle}>Introducción</Text>
                </View>
                <Text style={styles.paragraph}>
                  Se desarrolla el presente documento por encargo de la <Text style={styles.bold}>{comunidad.razonSocial || '______________________________'}</Text>,
                  con CIF <Text style={styles.bold}>{comunidad.nif || '________________________'}</Text>, al arquitecto F. Borja Angulo Angulo, colegiado
                  número <Text style={styles.bold}>423531</Text> por el CSCAE. / <Text style={styles.bold}>3458</Text> COAVN.
                </Text>
                <Text style={styles.paragraph}>
                  Los siguientes epígrafes, constituyen un Certificado de Accesibilidad relativo al edificio
                  residencial sito en <Text style={styles.bold}>{comunidad.direccion || '______________'}</Text>, con la finalidad de evaluar y dejar
                  constancia del grado de adecuación del mismo a los criterios y condiciones básicas de
                  accesibilidad universal de conformidad a la normativa técnica y legal vigente.
                </Text>
                <Text style={styles.paragraph}>
                  En concreto, es de aplicación el <Text style={styles.bold}>DECRET 209/2023, de 28 de noviembre</Text>, por el que se aprueba
                  el Codi d'accessibilitat de Catalunya y en especial, el contenido de su <Text style={styles.bold}>Capítulo Tercero</Text>,
                  en el que se desarrollan las condiciones de accesibilidad en el ámbito de la edificación.
                </Text>
                <Text style={styles.paragraph}>
                  El contenido del certificado se ha redactado a partir de la inspección directa del inmueble,
                  de la revisión de los datos catastrales, del análisis de la documentación técnica disponible
                  en su caso y de la comprobación de los elementos constructivos, dependencias, itinerarios y
                  dotaciones que inciden en las condiciones de accesibilidad del edificio. La información
                  recogida refleja fielmente el estado del inmueble en la fecha de la visita técnica.
                </Text>
                <Text style={styles.paragraph}>
                  El presente certificado se emite desde una posición de plena independencia profesional, con
                  criterios estrictamente técnicos y bajo los principios de objetividad, imparcialidad y
                  veracidad, sin que medie interés personal, económico o de otra índole que pudiera condicionar
                  las conclusiones alcanzadas. Las valoraciones y apreciaciones incluidas se fundamentan
                  exclusivamente en la normativa aplicable, la experiencia profesional y la observación directa
                  de la realidad física del edificio.
                </Text>
                <Text style={styles.paragraph}>
                  El alcance del presente documento se limita a la evaluación de las condiciones de
                  accesibilidad del edificio en relación con los aspectos analizados, no extendiéndose a otros
                  ámbitos técnicos no expresamente considerados ni a posibles modificaciones posteriores al
                  momento de la inspección. Las conclusiones y determinaciones que se recogen deben entenderse
                  referidas al estado actual del inmueble y a las condiciones existentes en el momento de la
                  emisión del certificado.
                </Text>
              </View>

              {/* <View style={styles.section} wrap={false}>
                <View style={styles.sectionHead}>
                  <Text style={styles.sectionNum}>02</Text>
                  <Text style={styles.sectionTitle}>Datos preliminares</Text>
                </View>
                <View style={styles.dataGrid}>
                  <View style={[styles.dataCellFull]}>
                    <Text style={styles.dataCellLabel}>Localización</Text>
                    <Text style={styles.dataCellValue}>{comunidad.direccion || '__'}</Text>
                  </View>
                  <View style={styles.dataCell}>
                    <Text style={styles.dataCellLabel}>Municipio</Text>
                    <Text style={styles.dataCellValue}>{comunidad.municipio || '__'}</Text>
                  </View>
                  <View style={[styles.dataCell, styles.dataCellRight]}>
                    <Text style={styles.dataCellLabel}>Referencia Catastral</Text>
                    <Text style={styles.dataCellValue}>{referenciaCatastral || '__'}</Text>
                  </View>
                  <View style={styles.dataCell}>
                    <Text style={styles.dataCellLabel}>Año de construcción</Text>
                    <Text style={styles.dataCellValue}>{anoConstruction || '__'}</Text>
                  </View>
                  <View style={[styles.dataCell, styles.dataCellRight]}>
                    <Text style={styles.dataCellLabel}>Perfil edificatorio</Text>
                    <Text style={styles.dataCellValue}>{perfilEdificatorio || '__'}</Text>
                  </View>
                  <View style={[styles.dataCell, styles.dataCellBottom]}>
                    <Text style={styles.dataCellLabel}>Nº de viviendas</Text>
                    <Text style={styles.dataCellValue}>{numViviendas || '__'}</Text>
                  </View>
                  <View style={[styles.dataCell, styles.dataCellRight, styles.dataCellBottom]}>
                    <Text style={styles.dataCellLabel}>
                      Dispone de ascensor {tieneAscensor === 'Sí' || tieneAscensor === 'Si' ? `(${numAscensores || '0'})` : ''}
                    </Text>
                    <Text style={styles.dataCellValue}>{tieneAscensor || 'No'}</Text>
                  </View>
                </View>
              </View> */}

              <View style={styles.section} wrap={false}>
                <View style={styles.sectionHead}>
                  <Text style={styles.sectionNum}>02</Text>
                  <Text style={styles.sectionTitle}>Comprobaciones realizadas y marco normativo</Text>
                </View>
                <Text style={styles.paragraph}>
                  El técnico ha girado visita al edificio a fecha <Text style={styles.bold}>{fechaStr || '_________________________'}</Text>,
                  haciendo un exhaustivo estudio de su configuración actual y su adecuación a la norma de
                  aplicación, que se centra en el <Text style={styles.bold}>DECRET 209/2023, de 28 de noviembre</Text>, por el que se aprueba
                  el Codi d'accessibilitat de Catalunya.
                </Text>
              </View>

              <View style={styles.section} break>
                <View style={styles.sectionHead}>
                  <Text style={styles.sectionNum}>03</Text>
                  <Text style={styles.sectionTitle}>Datos de inspección</Text>
                </View>
                <Text style={styles.paragraph}>
                  A continuación se recoge el detalle de los parámetros evaluados durante la inspección,
                  organizados por dimensión, con la indicación del artículo del DECRET 209/2023 que regula
                  el parámetro, el requisito normativo, el dato observado
                  <Text style={styles.italic}> in situ</Text> y el estado de cumplimiento.
                </Text>
                {sortDimsByCategory(normalizeAnswersCai(answersCai)).map((dim, di) => {
                  const bucket = gradeBucket(dim.porcentaje_categoria);
                  const fg = bucketFg(bucket);
                  const bg = bucketBg(bucket);
                  const border = bucketBorder(bucket);
                  const subs = Object.values(dim.subquestions || {});
                  const shortDim = subs.length <= 6;
                  
                  return (
                    <View key={di} style={styles.dim} wrap={!shortDim}>
                      <View style={styles.dimHead} wrap={false} minPresenceAhead={shortDim ? 0 : 90}>
                        <View style={styles.dimIconWrap}>
                          <DimIcon name={dim.nombre_categoria} color={fg} />
                        </View>
                        <Text style={styles.dimName}>{dim.nombre_categoria}</Text>
                        <View style={[styles.dimGrade, { backgroundColor: bg, borderColor: border }]}>
                          <Text style={[styles.dimGradeText, { color: fg }]}>{dim.porcentaje_categoria}</Text>
                          <Text style={[styles.dimGradePct, { color: fg }]}>%</Text>
                        </View>
                      </View>
                      <View style={styles.dimTable}>
                        <View style={styles.dimTableHead} wrap={false}>
                          <Text style={[styles.dimTableHeadCell, { width: '26%' }]}>Parámetro</Text>
                          <Text style={[styles.dimTableHeadCell, { width: '22%' }]}>Artículo</Text>
                          <Text style={[styles.dimTableHeadCell, { width: '22%' }]}>Requisito</Text>
                          <Text style={[styles.dimTableHeadCell, { width: '16%' }]}>Dato</Text>
                          <Text style={[styles.dimTableHeadCell, { width: '14%' }]}>Estado</Text>
                        </View>
                        {subs.map((sq, ri) => (
                          <View
                            key={ri}
                            style={[styles.dimRow, ri % 2 === 1 && styles.dimRowAlt]}
                            wrap={false}
                          >
                            <Text style={styles.cellParametro}>{sq?.parametro}</Text>
                            <Text style={styles.cellArticulo}>{sq?.anexo}</Text>
                            <Text style={styles.cellRequisito}>{sq?.questionCai?.requisito}</Text>
                            <Text style={styles.cellDato}>{sq?.resp}</Text>
                            <View style={styles.cellEstado}>
                              <EstadoChip cumplimiento={sq?.cumplimiento} />
                            </View>
                          </View>
                        ))}
                      </View>
                    </View>
                  );
                })}
              </View>

              <View style={styles.section} break>
                <View style={styles.sectionHead}>
                  <Text style={styles.sectionNum}>04</Text>
                  <Text style={styles.sectionTitle}>Incumplimientos detectados</Text>
                </View>
                <Text style={[styles.paragraph, { marginBottom: 12 }]}>
                  En atención al contenido del DECRET 209/2023, y en concreto del Capítulo Tercero que regula
                  las condiciones de accesibilidad en el ámbito de la edificación, no se ha podido evidenciar
                  el cumplimiento de los siguientes parámetros:
                </Text>
                <View style={styles.incumpHead} wrap={false}>
                  <Text style={[styles.incumpHeadCell, { width: 22 }]}>Nº</Text>
                  <Text style={[styles.incumpHeadCell, { width: 90 }]}>Dimensión</Text>
                  <Text style={[styles.incumpHeadCell, { flexGrow: 1 }]}>Parámetro</Text>
                  <Text style={[styles.incumpHeadCell, { width: 130, textAlign: 'right' }]}>Artículo</Text>
                </View>
                {incumplimientos.length === 0 ? (
                  <Text style={[styles.paragraph, { marginTop: 10 }]}>
                    No se detectaron incumplimientos.
                  </Text>
                ) : (
                  incumplimientos.map((row) => (
                    <View key={row.idx} style={styles.incumpRow} wrap={false}>
                      <Text style={styles.incumpIdx}>{String(row.idx).padStart(2, '0')}</Text>
                      <Text style={styles.incumpDim}>{row.dim}</Text>
                      <View style={styles.incumpParam}>
                        <Text style={styles.incumpParamTitle}>{row.paramTitle}</Text>
                        {!!row.detail && <Text style={styles.incumpDetail}>{row.detail}</Text>}
                      </View>
                      <View style={styles.incumpArticleCol}>
                        {!!row.article && (
                          <View style={styles.incumpArticlePill}>
                            <Text style={styles.incumpArticleText}>{row.article}</Text>
                          </View>
                        )}
                      </View>
                    </View>
                  ))
                )}
              </View>

              <View style={styles.section} wrap={false}>
                <View style={styles.sectionHead}>
                  <Text style={styles.sectionNum}>05</Text>
                  <Text style={styles.sectionTitle}>Conclusión · Análisis cumplimiento normativa de aplicación</Text>
                </View>

                <Text style={styles.paragraph}>
                  En base al marco normativo referenciado en el epígrafe anterior, el presente certificado
                  se articula en el análisis de los itinerarios, dependencias, dotaciones y equipamiento
                  del conjunto edificatorio.
                </Text>
                <Text style={styles.paragraph}>
                  Los parámetros para los que <Text style={styles.bold}>no se ha podido evidenciar el cumplimiento</Text>
                  {' '}del DECRET 209/2023 se identifican individualmente en el apartado «Datos de inspección»
                  del presente certificado, junto con el artículo de aplicación, el dato observado
                  <Text style={styles.italic}> in situ</Text> y la indicación de estado correspondiente.
                </Text>

                <View style={styles.resultBox}>
                  <View style={[
                    styles.resultScore,
                    { borderColor: bucketBorder(totalBucket), backgroundColor: bucketBg(totalBucket) },
                  ]}>
                    <Text style={[styles.resultLabel, { color: totalFg }]}>Grado de accesibilidad</Text>
                    <View style={styles.resultValue}>
                      <Text style={[styles.resultNum, { color: totalFg }]}>{isNaN(totalNum) ? '__' : totalNum}</Text>
                      <Text style={[styles.resultPct, { color: totalFgAccent }]}>%</Text>
                    </View>
                  </View>
                </View>

                <Text style={[styles.paragraph, { marginTop: 14 }]}>
                  Y para que así conste, se emite el presente Certificado de Accesibilidad, en el lugar y
                  fecha indicados, en prueba de conformidad y a los efectos oportunos.
                </Text>
                <Text style={styles.paragraph}>
                  En {lugarFirma || '________________________'}, a {diaFirma || '___'} de {mesFirma || '__________'} de {anoFirma || '20___'}.
                </Text>

                <View style={styles.signoff}>
                  <View style={styles.signoffPlace}>
                    <Text style={styles.signoffPill}>Firmado · Arquitecto colegiado</Text>
                    <Text style={styles.signoffDisclaimer}>
                      El presente certificado ha sido emitido por el técnico abajo firmante, conforme a
                      los principios de objetividad, imparcialidad y veracidad declarados en el epígrafe 01.
                    </Text>
                  </View>
                  <View style={styles.signature}>
                    <Image src={firmaArquitecto} style={styles.signatureImg} />
                    <Text style={styles.signatureName}>Angulo Arquitectura, S.L.P.</Text>
                    <Text style={styles.signatureSub}>F. Borja Angulo Angulo</Text>
                    <Text style={styles.signatureRole}>Arquitecto</Text>
                  </View>
                </View>
              </View>

              <View style={styles.pageFooter} fixed>
                <Text style={styles.pageFooterLeft}>Certificado de Accesibilidad · Integracces</Text>
                <Text style={styles.pageFooterRight} render={({ pageNumber, totalPages }) => `Página ${pageNumber} / ${totalPages}`} />
              </View>
            </Page>
          </Document>
        </PDFHandler>
      )}
    </>
  );
};

export default withRouter(CertificadoAccesibilidad);
